"use client"

import * as React from "react"
import { useReducedMotion } from "motion/react"

import { AskLeoButton } from "@/components/ask-leo-button"
import { LeoAskPopover } from "@/components/assessment-structure/leo-ask-popover"
import type { LeoAskChoice } from "@/components/assessment-structure/leo-ask-prompt"
import { StatusCell } from "@/components/data-views"
import { useLeoAmbience } from "@/components/leo-ambience-context"
import { LEO_DEEP_FILL_CLASS, LeoIcon } from "@/components/ui/leo-icon"
import {
  LeoCursorDots,
  useLeoCursorSpotlight,
} from "@/components/leo-cursor-dots"
import { FindingCopyEmphasis } from "@/components/question-quality/finding-copy-emphasis"
import {
  QuestionLeoUsagePerformanceHover,
} from "@/components/question-quality/question-leo-performance-insight"
import { LEO_BLOB_ANIM_RATE } from "@/lib/leo-ambience"
import { LeoSpottedBlobLayer } from "@/components/question-quality/leo-spotted-blob-layer"
import {
  LEO_USAGE_PHRASE_RE,
  leoJobsNote,
  leoJobsNoteEmphasis,
  qualitySuggestionLines,
  type LeoAuthoringJob,
  type QuestionQualityAlternate,
  type QuestionQualityCheck,
  type QuestionQualityHistory,
  type QuestionQualitySnapshot,
} from "@/lib/question-quality"
import {
  LIBRARY_AI_SPOTTED_LABEL,
  LIBRARY_AI_SPOTTED_PRESENTATION,
  type LibraryAiSpotted,
} from "@/lib/library-ai-spotted"
import { LEO_ENHANCE_WITH_LEO_LABEL } from "@/lib/question-quality/leo-cta-labels"
import { cn } from "@/lib/utils"

/** Compact Leo spotted card — smaller radius than the Ask Leo rail wash. */
const TOUCHPOINT_DOT_RADIUS = 72

export function LeoSpottedCard({
  ariaLabel,
  disabled,
  flipBlobs = false,
  dense = false,
  wash = "brand",
  children,
}: {
  ariaLabel: string
  disabled?: boolean
  /** Park the wash at the top. Compose keeps it at the bottom. */
  flipBlobs?: boolean
  /** One short row, after every spotted issue is fixed. */
  dense?: boolean
  /** Settled cards use the success green. Open findings keep the Leo wash. */
  wash?: "brand" | "success"
  children: React.ReactNode
}) {
  const { prefs } = useLeoAmbience()
  const reduceMotion = useReducedMotion() ?? false
  const showDots = Boolean(prefs.idleDots && !reduceMotion)
  const {
    spotlight,
    onPointerMove,
    onPointerEnter,
    onPointerLeave,
    onPointerDown,
  } = useLeoCursorSpotlight(showDots, {
    pulseWaves: Boolean(prefs.blobAnimations && !reduceMotion),
    pulseIntervalMs: Math.round(
      1600 * LEO_BLOB_ANIM_RATE[prefs.blobAnimationSpeed],
    ),
    pulseCount: 2,
  })
  const field = {
    ...spotlight,
    x: spotlight.active ? spotlight.x : 22,
    y: spotlight.active ? spotlight.y : flipBlobs ? 40 : 78,
    active: true,
  }

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      onPointerMove={onPointerMove}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onPointerDown={onPointerDown}
      className={cn(
        "relative isolate w-full overflow-hidden rounded-xl border border-brand/25 bg-background bg-clip-padding text-left shadow-xs",
        dense ? "p-2.5" : "p-3.5",
        disabled && "opacity-60",
      )}
    >
      <LeoSpottedBlobLayer wash={wash} />
      {showDots ? (
        <LeoCursorDots
          spotlight={field}
          radius={TOUCHPOINT_DOT_RADIUS}
          density={prefs.idleDensity}
          className="z-[1]"
        />
      ) : null}
      <span className={cn("relative z-[2] flex flex-col", dense ? "gap-0" : "gap-3")}>{children}</span>
    </div>
  )
}

const OPEN_ISSUE_ICON: Record<LibraryAiSpotted, string> = {
  "needs-fixing": "fa-circle-exclamation text-destructive",
  "worth-a-look": "fa-eye text-primary",
  "weak-performance": "fa-chart-line text-foreground",
  "looks-good": "fa-check text-success",
}

function LeoSpottedMark() {
  const reduced = useReducedMotion()
  const [settled, setSettled] = React.useState(false)
  React.useEffect(() => {
    if (reduced) {
      setSettled(true)
      return
    }
    const timer = window.setTimeout(() => setSettled(true), 480)
    return () => window.clearTimeout(timer)
  }, [reduced])

  return (
    <span
      aria-hidden
      className={cn(
        LEO_DEEP_FILL_CLASS,
        "inline-flex size-4 shrink-0 items-center justify-center leading-[0]",
      )}
    >
      <LeoIcon
        variant="ambient"
        size="xs"
        state={reduced || settled ? "rest" : "invited"}
        className="size-4"
      />
    </span>
  )
}

export type LeoSpottedFinding = {
  id: string
  label: string
  fixed: boolean
  stat?: boolean
  onSelect?: () => void
}

/** The question sheet's Leo spotted card. Compose uses this same card. */
export function LeoSpottedFindingsCard({
  headingId,
  issues,
  tag,
  allFixed = false,
  showEnhance = false,
  clarify,
  loadClarify,
  disabled,
  flipBlobs = false,
  appendBankOnly,
  onClarify,
  onEnhance,
  onCreateNew,
  onReplaceFromBank,
  aboveBar,
}: {
  headingId: string
  issues: readonly LeoSpottedFinding[]
  tag: LibraryAiSpotted
  allFixed?: boolean
  showEnhance?: boolean
  clarify?: { question: string; choices: readonly LeoAskChoice[] }
  loadClarify?: () => Promise<{ question: string; choices: LeoAskChoice[] }>
  disabled?: boolean
  flipBlobs?: boolean
  appendBankOnly?: boolean
  onClarify?: (instruction: string) => void
  /** Import settled card: Enhance with Leo. */
  onEnhance?: () => void
  onCreateNew?: () => void
  onReplaceFromBank?: () => void
  /** Classification stack or other Leo cards sit above Enhance / Create. */
  aboveBar?: React.ReactNode
}) {
  const openCount = issues.filter((issue) => !issue.fixed).length
  return (
    <LeoSpottedCard
      ariaLabel={allFixed ? "Looks good. Nothing else needs a change." : "Leo spotted"}
      flipBlobs={flipBlobs}
      dense={allFixed && !onEnhance && !onCreateNew}
      wash={allFixed ? "success" : "brand"}
      disabled={disabled}
    >
      {allFixed ? (
        <div
          className="leo-spotted-settled flex flex-col gap-3"
          role="status"
        >
          <div className="flex min-w-0 items-center gap-2.5">
            <h3 id={headingId} className="sr-only">
              Leo spotted
            </h3>
            <i
              className="leo-spotted-check-pop fa-kit fa-circle-check-star-christmas shrink-0 text-[1.75rem] leading-none"
              aria-hidden
            />
            <span className="min-w-0">
              <span className="block text-sm font-medium text-foreground">
                Looks good
              </span>
              <span className="block text-xs text-muted-foreground">
                Nothing else needs a change.
              </span>
            </span>
          </div>
          {onEnhance || onReplaceFromBank || onCreateNew ? (
            <div className="grid w-full grid-cols-[repeat(auto-fit,minmax(min(100%,14rem),1fr))] gap-2">
              {onEnhance ? (
                <AskLeoButton size="sm" variant="outline" label={LEO_ENHANCE_WITH_LEO_LABEL} showShortcut={false} animatedStar className="w-full justify-center bg-transparent" disabled={disabled} onClick={onEnhance} />
              ) : null}
              {onReplaceFromBank ? <button type="button" className="h-9 rounded-md border border-input bg-transparent px-3 text-sm font-medium" disabled={disabled} onClick={onReplaceFromBank}>Swap from bank</button> : null}
              {onCreateNew ? (
                <AskLeoButton size="sm" variant="outline" label="Create new with Leo" showShortcut={false} className="w-full justify-center bg-transparent" disabled={disabled} onClick={onCreateNew} />
              ) : null}
            </div>
          ) : null}
        </div>
      ) : (
        <>
          <div className="flex w-full min-w-0 items-center justify-between gap-2">
            <h3
              id={headingId}
              className="inline-flex min-w-0 items-center gap-1.5 text-xs font-semibold text-muted-foreground"
            >
              <LeoSpottedMark />
              Leo spotted
            </h3>
            {openCount > 0 ? (
              <StatusCell
                label={LIBRARY_AI_SPOTTED_LABEL[tag]}
                tone={LIBRARY_AI_SPOTTED_PRESENTATION[tag].tone}
                icon={LIBRARY_AI_SPOTTED_PRESENTATION[tag].icon}
              />
            ) : null}
          </div>
          <ul className="flex flex-col divide-y divide-border">
            {issues.map((issue) => (
              <li
                key={issue.id}
                className="flex items-center justify-between gap-3 py-2 text-sm text-foreground"
                aria-label={`${issue.label}, ${issue.fixed ? "fixed" : "open"}`}
              >
                {issue.onSelect && !issue.fixed ? (
                  <button
                    type="button"
                    className="min-w-0 flex-1 rounded-sm text-left text-sm text-foreground underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    disabled={disabled}
                    onClick={issue.onSelect}
                  >
                    {issue.label}
                  </button>
                ) : (
                  <span>{issue.label}</span>
                )}
                <i
                  className={cn(
                    "fa-light w-4 shrink-0 text-center",
                    issue.fixed
                      ? "fa-check text-success"
                      : OPEN_ISSUE_ICON[issue.stat ? "weak-performance" : tag],
                  )}
                  aria-hidden
                />
              </li>
            ))}
          </ul>
          {aboveBar ? <div className="min-w-0">{aboveBar}</div> : null}
          {(showEnhance && (onEnhance || (clarify && onClarify))) || onReplaceFromBank || onCreateNew ? (
            <div className="grid w-full grid-cols-[repeat(auto-fit,minmax(min(100%,14rem),1fr))] gap-2">
              {showEnhance && onEnhance ? (
                <AskLeoButton size="sm" variant="outline" label={LEO_ENHANCE_WITH_LEO_LABEL} showShortcut={false} animatedStar className="w-full justify-center" disabled={disabled} onClick={onEnhance} />
              ) : showEnhance && clarify && onClarify ? (
                <LeoAskPopover question={clarify.question} choices={clarify.choices} loadClarify={loadClarify} disabled={disabled} triggerLabel={LEO_ENHANCE_WITH_LEO_LABEL} triggerSize="sm" triggerClassName="w-full" align="start" side="top" contentClassName="z-[80]" appendBankOnly={appendBankOnly} onSubmit={onClarify} />
              ) : null}
              {onReplaceFromBank ? <button type="button" className="h-9 rounded-md border border-input bg-transparent px-3 text-sm font-medium" disabled={disabled} onClick={onReplaceFromBank}>Swap from bank</button> : null}
              {onCreateNew ? (
                <AskLeoButton size="sm" variant="outline" label="Create new with Leo" showShortcut={false} className="w-full justify-center" disabled={disabled} onClick={onCreateNew} />
              ) : null}
            </div>
          ) : null}
        </>
      )}
    </LeoSpottedCard>
  )
}

function LeoJobsNote({
  note,
  history,
  jobs,
}: {
  note: string
  history: QuestionQualityHistory | null
  jobs: LeoAuthoringJob[]
}) {
  const phrases = leoJobsNoteEmphasis(jobs)

  if (!history?.sat || !LEO_USAGE_PHRASE_RE.test(note)) {
    return <FindingCopyEmphasis text={note} phrases={phrases} />
  }

  const parts = note.split(LEO_USAGE_PHRASE_RE)
  return (
    <span>
      {parts.map((part, index) => {
        if (!part) return null
        if (LEO_USAGE_PHRASE_RE.test(part)) {
          return (
            <QuestionLeoUsagePerformanceHover
              key={part}
              phrase={part}
              history={history}
            />
          )
        }
        return (
          <FindingCopyEmphasis
            key={part}
            text={part}
            phrases={phrases}
          />
        )
      })}
    </span>
  )
}

export function LeoSpottedClassificationApply({
  check,
  snapshot,
  disabled,
  applying,
  onApply,
}: {
  check: QuestionQualityCheck
  snapshot?: QuestionQualitySnapshot
  disabled?: boolean
  applying?: boolean
  onApply: (
    check: QuestionQualityCheck,
    alternate?: QuestionQualityAlternate,
  ) => void
}) {
  const alternate = check.alternates?.[0]
  const lines = alternate
    ? qualitySuggestionLines(check, alternate, snapshot)
    : []
  const note = check.mechanism
  return (
    <LeoSpottedCard
      ariaLabel={`${note} Apply`}
      disabled={disabled}
    >
      <span className="text-[0.8125rem] leading-5 text-foreground">{note}</span>
      {lines.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {lines.map((line) => (
            <li
              key={line.text}
              className="text-sm font-medium leading-snug text-foreground"
            >
              {line.text}
            </li>
          ))}
        </ul>
      ) : null}
      <AskLeoButton
        size="sm"
        variant="outline"
        label="Apply"
        showShortcut={false}
        animatedStar
        aria-busy={applying}
        busyLabel="Applying"
        className="w-fit bg-transparent"
        disabled={disabled || applying}
        onClick={() => onApply(check, alternate)}
      />
    </LeoSpottedCard>
  )
}

export function QuestionLeoJobs({
  jobs,
  history = null,
  disabled,
  onAskLeo,
}: {
  jobs: LeoAuthoringJob[]
  history?: QuestionQualityHistory | null
  disabled?: boolean
  onAskLeo: () => void
}) {
  const [opening, setOpening] = React.useState(false)
  const openingTimerRef = React.useRef(0)

  React.useEffect(
    () => () => window.clearTimeout(openingTimerRef.current),
    [],
  )

  const handleAskLeo = React.useCallback(() => {
    if (disabled || opening) return
    setOpening(true)
    onAskLeo()
    window.clearTimeout(openingTimerRef.current)
    openingTimerRef.current = window.setTimeout(() => setOpening(false), 680)
  }, [disabled, onAskLeo, opening])

  if (jobs.length === 0) return null

  const note = leoJobsNote(jobs)

  return (
    <LeoSpottedCard ariaLabel={`Fix with Leo. ${note}`} disabled={disabled}>
      <span className="text-[0.8125rem] leading-5 text-foreground">
        <LeoJobsNote note={note} history={history} jobs={jobs} />
      </span>
      <AskLeoButton
        size="sm"
        variant="outline"
        label="Fix with Leo"
        ariaLabel="Fix with Leo"
        tooltipLabel="Fix with Leo"
        showShortcut={false}
        animatedStar
        aria-busy={opening}
        busyLabel="Opening"
        className="w-fit bg-transparent"
        disabled={disabled}
        onClick={handleAskLeo}
      />
    </LeoSpottedCard>
  )
}
